const Conditional = require("./conditional");

class ConditionalFactory {
  constructor() {
    this.cache = {};
  }
  create(str, options = {}) {
    this.recurseLog = [];

    const { throwOnFail = false, cache = true } = options;

    if (typeof str !== "string") {
      return new Conditional();
    }

    if (this.cache[str] && cache) {
      const cached = this.cache[str];
      return new Conditional(
        cached.property,
        cached.operator,
        cached.value,
        cached.unitSpecifier
      );
    }

    const normalized = this.normalize(str);
    let conditional;
    try {
      conditional = this.parse(normalized);
    } catch (e) {
      if (throwOnFail) {
        throw e;
      } else {
        if (
          !(
            typeof normalized === "string" &&
            normalized.startsWith("***") &&
            normalized.endsWith("***")
          )
        ) {
          console.warn(`Could not parse conditionals. ${normalized}`); // this will be ignored from the caller
        }
        return null;
      }
    }

    if (cache) {
      this.cache[str] = { ...conditional };
    }

    return conditional;
  }

  validateInput(str) {
    const statements = (str.match(/=/g) || []).length;
    const openingBraces = (str.match(/\(/g) || []).length;
    const closingBraces = (str.match(/\)/g) || []).length;
    if (
      statements < 1 ||
      openingBraces !== closingBraces ||
      str.indexOf("!|") > -1
    ) {
      throw new Error(`Invalid conditional statement: ${str}`);
    }
  }

  getOperator(str) {
    if (str.indexOf("&") > -1) {
      return "&&";
    }

    if (str.indexOf("|") > -1 || str.indexOf(",") > -1) {
      return "||";
    }

    if (str.indexOf("~") > -1) {
      return str.indexOf("!") > -1 ? "!~" : "~";
    }

    if (str.indexOf("!") > -1) {
      return "!=";
    }

    return "=";
  }

  getProperty(str) {
    const property = "statement";

    if (str.indexOf("&") > -1) {
      return property;
    }

    if (str.indexOf("|") > -1) {
      return property;
    }

    const parts = str.split("=");
    return parts[0].replace("!", "");
  }

  getValue(operator, str) {
    if (typeof str !== "string") {
      throw new Error("Value must be string.");
    }

    const op = operator === "||" ? "," : operator;

    const split = str.split("=");
    let value = split.length > 1 ? split[1] : split[0];

    value = value
      .split(op)
      .map((v) => v.split(":"))
      .map((v) => {
        if (v.length > 1) {
          this.unitSpecifier = v[1];
        }
        return v[0];
      })
      .map((v) => this.convertNumber(v));

    if (value.length === 1) {
      value = this.convertBoolean(value[0]);
    }

    return value;
  }

  getFirstParenthesisMatch(str) {
    let level = 0;
    let match = null;
    for (let i = 0; i < str.length; i++) {
      const character = str[i];

      if (character === ")") {
        if (level === 1) {
          match = i;
          break;
        }
        level -= 1;
      }

      if (character === "(") {
        level += 1;
      }
    }
    return match;
  }

  parenthesisAreOutside(str) {
    if (str[0] !== "(") {
      return false;
    }
    const lastIndex = str.length - 1;
    const match = this.getFirstParenthesisMatch(str);
    return match === lastIndex;
  }

  peelParentheses(str) {
    if (str.length < 2) {
      return str;
    }

    const lastIndex = str.length - 1;

    // Peel away parenthesis, but only if the statement won't break
    if (this.parenthesisAreOutside(str)) {
      const peeled = str.substring(1, lastIndex);
      str = peeled;
    }
    return str;
  }

  parse(string) {
    this.recurseLog.push(string);

    if (this.recurseLog.length > 1000) {
      throw new Error("Too much recursion in conditional statement.");
    }

    this.validateInput(string);
    const conditional = new Conditional();
    let str = this.peelParentheses(string);
    const statements = (str.match(/=/g) || []).length;
    if (statements < 1) {
      return conditional;
    }

    const parentheses =
      str.match(/\((?:[^)(]+|\((?:[^)(]+|\([^)(]*\))*\))*\)/g) || [];
    const tokens = parentheses.map((section, i) => {
      const token = `${i}PARENTH`;
      return {
        section,
        token,
      };
    });

    tokens.forEach((token) => {
      str = str.replace(token.section, token.token);
    });

    conditional.property = this.getProperty(str);
    conditional.operator = this.getOperator(str);

    const operators = ["&&", "||", "!||"];

    if (
      conditional.property === "statement" &&
      operators.find((operator) => conditional.operator === operator)
    ) {
      conditional.value = str.split(conditional.operator).map((statement) => {
        let input = statement;

        // Replace the tokens back, so that we parse the contents
        tokens.forEach((tok) => {
          input = input.replace(tok.token, tok.section);
        });
        return this.parse(input);
      });
      return conditional;
    }

    tokens.forEach((tok) => {
      str = str.replace(tok.token, tok.section);
    });

    conditional.value = this.getValue(conditional.operator, str);
    return conditional;
  }

  normalize(str) {
    str = str.replace(/ /g, "");
    str = str.replace(/\|{2,}/g, "|");
    str = str.replace(/\&{2,}/g, "&");
    str = str.replace(/OR/g, "|");
    str = str.replace(/\|/g, "||");
    str = str.replace(/,/g, ",");
    str = str.replace(/\&/g, "&&");
    return str;
  }

  convertNumber(value) {
    if (!isNaN(value)) {
      if (value.indexOf(".") > -1) {
        value = parseFloat(value);
      } else {
        value = parseInt(value, 10);
      }
    }
    return value;
  }

  convertBoolean(value) {
    if (typeof value === "string") {
      if (value.toLowerCase() === "true") {
        return true;
      }

      if (value.toLowerCase() === "false") {
        return false;
      }
    }

    return value;
  }
}

module.exports = new ConditionalFactory();
